import { Palette } from "lucide-react";

export const MAT_COLOR = "Mat";

export const colorSlug = (c) => (c || "").toLowerCase().replace(/\s+/g, "-");

export const selKey = (materialId, color) => `${materialId}::${color}`;

// Purchase price for a material in a given color. "Mat" = base price.
export function colorPurchasePrice(material, color) {
  const base = Number(material.purchase_price || 0);
  if (!color || color === MAT_COLOR) return base;
  const t = color.trim().toLowerCase();
  const found = (material.colors || []).find(
    (c) => (c.name || "").trim().toLowerCase() === t
  );
  if (!found) return base;
  const p = Number(found.purchase_price);
  return Number.isFinite(p) ? p : base;
}

// Which of the globally selected colors apply to this material.
// "Mat" always applies; other colors only if the material defines them.
// Falls back to ["Mat"] so every material stays visible in the list.
export function applicableColors(material, selectedColors) {
  const names = new Set(
    (material.colors || []).map((c) => (c.name || "").trim().toLowerCase())
  );
  const list = (selectedColors || []).filter(
    (c) => c === MAT_COLOR || names.has(c.trim().toLowerCase())
  );
  return list.length ? list : [MAT_COLOR];
}

/**
 * Multi-select color chips shared by Calculator + Deviz.
 * `colors`: union of color names across materials (without "Mat").
 * `selected`: array of active color names (always at least one).
 */
export function ColorPicker({ colors, selected, onChange, testIdPrefix = "color" }) {
  const all = [MAT_COLOR, ...(Array.isArray(colors) ? colors : [])];
  const isOn = (c) =>
    (selected || []).some((s) => s.toLowerCase() === c.toLowerCase());
  const toggle = (c) => {
    const cur = selected || [];
    if (isOn(c)) {
      const next = cur.filter((s) => s.toLowerCase() !== c.toLowerCase());
      onChange(next.length ? next : [MAT_COLOR]);
    } else {
      onChange([...cur, c]);
    }
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testIdPrefix}-picker`}>
      <Palette size={13} className="text-zinc-500" />
      <span className="font-mono uppercase text-[10px] tracking-widest text-zinc-500 mr-1">
        Culori:
      </span>
      {all.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => toggle(c)}
          className={`px-2.5 py-1 rounded-sm border font-mono uppercase text-[10px] tracking-wider transition ${
            isOn(c)
              ? "bg-black text-white border-black"
              : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
          }`}
          data-testid={`${testIdPrefix}-${colorSlug(c)}`}
        >
          {c}
        </button>
      ))}
      <span className="font-mono text-[9px] text-zinc-400 ml-1">
        (selecteaza mai multe pentru cantitati per culoare)
      </span>
    </div>
  );
}

// Union of color names across all materials, deduplicated case-insensitively.
export function collectAllColors(materials) {
  const seen = new Map();
  for (const m of materials || []) {
    for (const c of m.colors || []) {
      const name = (c.name || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (!seen.has(key)) seen.set(key, name);
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "ro"));
}
